import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { Action } from 'state/actions';
import { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import { ExposedFormStatus } from 'state/forms/models';
import {
  initialSecondaryMarketBatchState,
  OrderTypeState,
  Row,
  RowData,
  SecondaryMarketBatchState,
} from 'state/workspaces/secondary-market/batch/model';
import { LoadingStatus } from 'utils/status';

import { replaceRowsById } from './utils';

export function secondaryMarketBatchReducer(
  state: SecondaryMarketBatchState = initialSecondaryMarketBatchState,
  action: Action,
): SecondaryMarketBatchState {
  switch (action.type) {
    case 'SECONDARY_MARKET_BATCH_ORDER_TYPE_CHANGED':
      return {
        ...state,
        orderType: action.payload.orderType,
      };
    case 'SECONDARY_MARKET_BATCH_SEARCH_TYPE_CHANGED':
      return {
        ...state,
        stateByOrderType: {
          ...state.stateByOrderType,
          [action.payload.orderType]: {
            ...state.stateByOrderType[action.payload.orderType],
            searchType: action.payload.searchType,
          },
        },
      };
    case 'SECONDARY_MARKET_BATCH_RELOAD_REQUESTED':
      return {
        ...state,
        stateByOrderType: {
          ...state.stateByOrderType,
          [action.payload.orderType]: { ...state.stateByOrderType[action.payload.orderType], status: 'loading' },
        },
      };
    case 'SECONDARY_MARKET_BATCH_RELOAD_SUCCEEDED':
      return {
        ...state,
        stateByOrderType: {
          ...state.stateByOrderType,
          [action.payload.orderType]: {
            ...state.stateByOrderType[action.payload.orderType],
            status: 'success',
            content: {
              ...action.payload.response,
              preference: action.payload.response.preference?.data,
              columnsDefinitions: action.payload.response.columnsDefinitions.map(({ metadata, ...col }) => ({
                ...col,
                enableCellChangeFlash: true,
                context: metadata ?? {},
              })) satisfies IBlotterColumnDefinition<RowData>[],
              rows: action.payload.response.rows.map(({ metadata, ...row }) => {
                const rowData = { ...row.data, modifyStatus: 'idle' as const, metadata };
                return {
                  ...row,
                  selected: false,
                  data: rowData,
                  operationStatus: 'Warning' as const,
                };
              }),
            },
          },
        },
      };
    case 'SECONDARY_MARKET_BATCH_RELOAD_FAILED':
      return {
        ...state,
        stateByOrderType: {
          ...state.stateByOrderType,
          [action.payload.orderType]: {
            ...state.stateByOrderType[action.payload.orderType],
            status: 'error',
            error: action.payload.error,
          },
        },
      };
    case 'SECONDARY_MARKET_BATCH_SEARCH_SUCCEEDED':
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: [
            ...action.payload.response.rows.map(({ metadata, ...row }) => {
              const rowData = { ...row.data, modifyStatus: 'idle' as const, metadata };
              return {
                ...row,
                selected: false,
                data: rowData,
                editableState: {},
              };
            }),
            ...subState.content.rows,
          ],
        },
      }));

    case 'SECONDARY_MARKET_BATCH_MODIFY_REQUESTED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.map(row => {
            const updatedValue = action.payload.rows.find(r => r.rowId === row.data.rowId);
            if (updatedValue) {
              return {
                ...row,
                data: {
                  ...updatedValue,
                  modifyStatus: 'loading',
                },
                editableState: {},
              };
            }
            return row;
          }),
        },
      }));
    }

    case 'SECONDARY_MARKET_BATCH_RESET_FAILED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.map(row => {
            const isModifying = action.payload.rowsIds.some(rowId => rowId === row.data.rowId);
            if (isModifying) {
              return {
                ...row,
                data: {
                  ...row.data,
                  modifyStatus: 'error',
                  metadata: { ...row.data.metadata, messages: [], messageByColumns: {} },
                },
              };
            }
            return row;
          }),
        },
      }));
    }

    case 'SECONDARY_MARKET_BATCH_RESET_REQUESTED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.map(row => {
            const isReseting = action.payload.request.rowIds.includes(row.data.rowId);
            if (isReseting) {
              return {
                ...row,
                data: {
                  ...row.data,
                  modifyStatus: 'loading',
                  metadata: { ...row.data.metadata, messages: [], messageByColumns: {} },
                },
                editableState: {},
              };
            }
            return row;
          }),
        },
      }));
    }

    case 'SECONDARY_MARKET_BATCH_RESET_SUCCEEDED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.map(row => {
            const resetRow = action.payload.response.rows.find(r => r.data.rowId === row.data.rowId);
            if (resetRow) {
              return {
                ...row,
                data: {
                  ...resetRow.data,
                  modifyStatus: 'success',
                  metadata: { ...row.data.metadata, messages: [], messageByColumns: {} },
                },
              };
            }
            return row;
          }),
        },
      }));
    }

    case 'SECONDARY_MARKET_BATCH_MODIFY_SUCCEEDED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: replaceRowsById(subState.content.rows, action.payload.response.rows, 'success'),
        },
      }));
    }
    case 'SECONDARY_MARKET_BATCH_MODIFY_FAILED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: mergeRowsById(subState.content.rows, action.payload.rows, 'error'),
        },
      }));
    }

    case 'SECONDARY_MARKET_BATCH_SET_CELL_EDITABLE_INFO': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.map(row => {
            if (row.data.rowId === action.payload.rowId) {
              return {
                ...row,
                editableState: {
                  ...row.editableState,
                  [action.payload.field]: action.payload.info,
                },
              };
            }
            return row;
          }),
        },
      }));
    }

    case 'SECONDARY_MARKET_BATCH_UPDATE_PREFERENCE': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          preference: action.payload.preference,
        },
      }));
    }
    case 'SECONDARY_MARKET_BATCH_SELECTED_ROWS_CHANGED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.map(row => ({
            ...row,
            selected: action.payload.rows.find(r => r.id === row.data.rowId)?.selected ?? false,
          })),
        },
      }));
    }
    case 'SECONDARY_MARKET_BATCH_DELETE_ROWS_SUCCEEDED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.filter(row => !action.payload.rowIds.includes(row.data.rowId)),
        },
      }));
    }
    case 'SECONDARY_MARKET_BATCH_DELETE_ROWS_REQUESTED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.map(row => {
            const rowToDelete = action.payload.rowIds.find(rowId => rowId === row.data.rowId);
            if (rowToDelete) {
              return { ...row, data: { ...row.data, modifyStatus: 'loading' } };
            }
            return row;
          }),
        },
      }));
    }
    case 'SECONDARY_MARKET_BATCH_OPERATION_REQUESTED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.map(row => {
            const rowToOperate = action.payload.rowIds.find(rowId => rowId === row.data.rowId);
            if (rowToOperate) {
              return {
                ...row,
                selected: false,
                data: {
                  ...row.data,
                  operationStatus: 'InProgress' satisfies ExposedFormStatus,
                  metadata: { ...row.data.metadata, messages: [], messageByColumns: {} },
                },
              };
            }
            return row;
          }),
        },
      }));
    }
    case 'SECONDARY_MARKET_BATCH_OPERATION_SUCCEEDED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: mergeRowsById(
            subState.content.rows,
            action.payload.rows.map(({ data, metadata }) => ({ ...data, metadata })),
          ),
        },
      }));
    }
    case 'SECONDARY_MARKET_BATCH_OPERATION_FAILED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.map(row => {
            const rowToOperate = action.payload.rowIds.find(rowId => rowId === row.data.rowId);
            if (rowToOperate) {
              return { ...row, data: { ...row.data, operationStatus: 'Rejected' satisfies ExposedFormStatus } };
            }
            return row;
          }),
        },
      }));
    }
    case 'SECONDARY_MARKET_BATCH_ROW_QUOTE_EXPIRED': {
      return doIfSucceeded(state, action.payload.orderType, subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.map(row => {
            if (row.data.rowId !== action.payload.rowId) {
              return row;
            }
            return {
              ...row,
              data: {
                ...row.data,
                operationStatus: 'Expired',
                quoteId: undefined,
                settlementPrice: undefined,
                settlementDate: undefined,
                tradingPrice: undefined,
                cashAmount: undefined,
                tradeDate: undefined,
                deductedFee: undefined,
                accruedCoupon: undefined,
                upfrontFee: undefined,
                metadata: {
                  ...row.data.metadata,
                  availableActions: [
                    { type: 'quote', enabled: true },
                    { type: 'open-in-secondary-market', enabled: true },
                  ],
                  timeToTradeInSeconds: undefined,
                },
              },
            };
          }),
        },
      }));
    }
    case 'SECONDARY_MARKET_BATCH_CLEAR_QUOTE_DATA': {
      return doIfSucceeded(state, 'ebiz-quote', subState => ({
        ...subState,
        content: {
          ...subState.content,
          rows: subState.content.rows.map(row => {
            if (row.data.rowId !== action.payload.rowId) {
              return row;
            }
            return {
              ...row,
              data: {
                ...row.data,
                operationStatus: 'Ready',
                quoteId: undefined,
                settlementPrice: undefined,
                tradingPrice: undefined,
                cashAmount: undefined,
                tradeDate: undefined,
                deductedFee: undefined,
                accruedCoupon: undefined,
                upfrontFee: undefined,
                metadata: {
                  ...row.data.metadata,
                  availableActions: row.data.metadata.availableActions
                    .filter(action => action.type !== 'trade')
                    .concat({ type: 'quote', enabled: true }),
                  timeToTradeInSeconds: undefined,
                },
              },
            };
          }),
        },
      }));
    }
  }
  return state;
}

function doIfSucceeded(
  state: SecondaryMarketBatchState,
  orderType: OrderType,
  callback: (subState: OrderTypeState & { status: 'success' }) => OrderTypeState,
): SecondaryMarketBatchState {
  const subState = state.stateByOrderType[orderType];
  if (subState.status !== 'success') {
    return state;
  }
  return {
    ...state,
    stateByOrderType: {
      ...state.stateByOrderType,
      [orderType]: callback(subState),
    },
  };
}

function mergeRowsById(rows: Row[], incomingRows: readonly Partial<RowData>[], modifyStatus?: LoadingStatus): Row[] {
  return rows.map(row => {
    const incoming = incomingRows.find(incomingRow => incomingRow.rowId === row.data.rowId);
    if (!incoming) {
      return row;
    }
    return {
      ...row,
      data: {
        ...row.data,
        ...incoming,
        metadata: {
          ...row.data.metadata,
          ...(incoming.metadata ?? {}),
        },
        modifyStatus: modifyStatus ?? row.data.modifyStatus,
      },
    };
  });
}
